import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["menu"]
  static values = {
    lockMs: { type: Number, default: 300 }
  }

  connect() {
    this._lastInteractionWasKeyboard = false
    this._toggleLock = false
    this._focusTargetOnClose = null

    // Keyboard: Enter/Space → click (delegated to toggle via data-action)
    this.element.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault()
        this._lastInteractionWasKeyboard = true
        if (!this._toggleLock) {
          this._toggleLock = true
          setTimeout(() => { this._toggleLock = false }, this.lockMsValue)
          this.toggle()
        }
      }
    })

    // Click outside → close
    this._outsideClick = (e) => {
      if (this.isOpen && !this.element.contains(e.target) && !this.menuTarget.contains(e.target)) {
        this.close()
      }
    }

    // ESC → close (global, used when focus trap is not active)
    this._escKey = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close()
      }
    }
  }

  toggle() {
    if (this._toggleLock) return
    this._toggleLock = true
    setTimeout(() => { this._toggleLock = false }, this.lockMsValue)

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    // Coordinate with searchbox: close it before opening menu
    const openSearch = document.querySelector('button[data-controller="searchbox"][aria-expanded="true"]')
      || (document.getElementById('searchBox') && document.getElementById('searchBox').getAttribute('aria-hidden') === 'false')
    if (openSearch) {
      document.dispatchEvent(new CustomEvent('search:close'))
      let fallbackTimer
      const onClosed = () => {
        clearTimeout(fallbackTimer)
        document.removeEventListener('search:closed', onClosed)
        this._doOpen()
      }
      document.addEventListener('search:closed', onClosed, { once: true })
      fallbackTimer = setTimeout(() => {
        document.removeEventListener('search:closed', onClosed)
        this._doOpen()
      }, 600)
      return
    }
    this._doOpen()
  }

  _doOpen() {
    this.element.setAttribute('aria-expanded', 'true')
    this.menuTarget.setAttribute('aria-hidden', 'false')
    this.menuTarget.classList.add('open')
    document.body.classList.add('no-scroll')
    document.querySelector('header')?.classList.add('open')

    this._updateLabel(true)
    this._hideScroll()

    document.addEventListener('click', this._outsideClick)
    document.addEventListener('keydown', this._escKey)

    // Focus first menu item if opened via keyboard
    if (this._lastInteractionWasKeyboard) {
      this._focusFirstMenuItem()
      this._lastInteractionWasKeyboard = false
    }

    this._setupFocusTrap()
  }

  close() {
    const wasOpen = this.isOpen

    this.element.setAttribute('aria-expanded', 'false')
    this.menuTarget.setAttribute('aria-hidden', 'true')
    this.menuTarget.classList.remove('open')
    document.body.classList.remove('no-scroll')
    document.querySelector('header')?.classList.remove('open')

    this._updateLabel(false)
    this._showScroll()

    document.removeEventListener('click', this._outsideClick)
    document.removeEventListener('keydown', this._escKey)

    this._removeFocusTrap()

    // Return focus to button on close
    if (wasOpen) {
      this._lastInteractionWasKeyboard = false
      this.element.focus()
    }

    // If there's a pending focus target (Tab out), focus it
    if (this._focusTargetOnClose) {
      const target = this._focusTargetOnClose
      this._focusTargetOnClose = null
      setTimeout(() => { try { target.focus() } catch (e) { /* noop */ } }, 10)
    }
  }

  get isOpen() {
    return this.element.getAttribute('aria-expanded') === 'true'
  }

  // ── Label ──

  _updateLabel(isOpen) {
    const label = this.element.querySelector('.menu-label')
    const openText = this.element.dataset.openText || 'Menu'
    const closeText = this.element.dataset.closeText || 'Cerrar'
    if (label) {
      label.textContent = isOpen ? closeText : openText
      this.element.setAttribute('aria-label', isOpen ? closeText : openText)
    }
  }

  // ── Scroll lock ──

  _hideScroll() {
    document.body.dataset.scrollPosition = window.scrollY
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${window.scrollY}px`
  }

  _showScroll() {
    const scrollPosition = document.body.dataset.scrollPosition
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.top = ''
    if (scrollPosition) {
      window.scrollTo(0, Number(scrollPosition))
    }
  }

  // ── Focus first menu item ──

  _focusFirstMenuItem() {
    const nav = this.menuTarget
    if (!nav) return
    requestAnimationFrame(() => {
      let attempts = 0
      const tryFocus = () => {
        const focusables = Array.from(nav.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])'))
          .filter(el => el.offsetParent !== null || el.getClientRects().length)
        if (focusables.length > 0) {
          focusables[0].focus()
          return true
        }
        return false
      }
      setTimeout(() => {
        if (!tryFocus()) {
          const interval = setInterval(() => {
            attempts++
            if (tryFocus() || attempts > 6) clearInterval(interval)
          }, 80)
        }
      }, 30)
    })
  }

  // ── Focus trap ──

  _setupFocusTrap() {
    const nav = this.menuTarget
    const button = this.element
    if (!nav) return

    this._focusTrapHandler = (e) => {
      if (button.getAttribute('aria-expanded') !== 'true') return

      const focusables = Array.from(nav.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])'))
        .filter(el => el.offsetParent !== null || el.getClientRects().length)
      if (focusables.length === 0) return

      const first = focusables[0]
      const last = focusables[focusables.length - 1]

      const getAllFocusable = () =>
        Array.from(document.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
          .filter(el => el.offsetParent !== null || el.getClientRects().length)

      const findNext = (el) => {
        const logoImg = document.querySelector('header img[src*="logo"]')
        const logoAnchor = logoImg ? logoImg.closest('a') : null
        if (logoAnchor) return logoAnchor
        const all = getAllFocusable()
        const idx = all.indexOf(el)
        return (idx >= 0 && idx < all.length - 1) ? all[idx + 1] : null
      }

      const findPrev = (el) => {
        const all = getAllFocusable()
        const idx = all.indexOf(el)
        return (idx > 0) ? all[idx - 1] : null
      }

      // Escape → close and return focus to button
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault()
        this._focusTargetOnClose = button
        this.close()
        setTimeout(() => { try { button.focus() } catch (er) { /* noop */ } }, 50)
        return
      }

      // Tab from last → close and move focus out
      if (e.key === 'Tab' && !e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        const next = findNext(last)
        this._focusTargetOnClose = next
        this.close()
        setTimeout(() => {
          if (next && button.getAttribute('aria-expanded') === 'false') {
            try { next.focus() } catch (e) { /* noop */ }
            this._focusTargetOnClose = null
          }
        }, 200)
      }

      // Shift+Tab from first → close and move focus out
      if (e.key === 'Tab' && e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        const prev = findPrev(first)
        this._focusTargetOnClose = prev
        this.close()
        setTimeout(() => {
          if (prev && button.getAttribute('aria-expanded') === 'false') {
            try { prev.focus() } catch (e) { /* noop */ }
            this._focusTargetOnClose = null
          }
        }, 200)
      }
    }

    document.addEventListener('keydown', this._focusTrapHandler)
  }

  _removeFocusTrap() {
    if (this._focusTrapHandler) {
      document.removeEventListener('keydown', this._focusTrapHandler)
      this._focusTrapHandler = null
    }
  }

  // ── Cleanup ──

  disconnect() {
    this._removeFocusTrap()
    document.removeEventListener('click', this._outsideClick)
    document.removeEventListener('keydown', this._escKey)
  }
}
